/**
 * 模板管理页面
 * 管理数据类、定开类、产品类、两轮车类的标书模板
 */
import React, { useState, useEffect } from 'react';
import { DocumentDuplicateIcon, PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { ProjectType } from '../types';

const TemplateManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<ProjectType>('通用类');

  // 模拟加载
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const projectTypes: ProjectType[] = ['通用类', '数据类', '定开类', '产品类', '两轮车'];

  // 模板类型定义
  interface TemplateItem {
    id: string;
    name: string;
    description: string;
    sections: number;
  }

  // 模拟模板数据
  const mockTemplates: Record<ProjectType, TemplateItem[]> = {
    '通用类': [
      { id: '0', name: '通用标书模板', description: '适用于各类通用项目', sections: 10 },
    ],
    '数据类': [
      { id: '1', name: '数据服务标准模板', description: '适用于数据服务类项目', sections: 12 },
      { id: '2', name: '地图数据采购模板', description: '地图数据采购专用', sections: 10 },
    ],
    '定开类': [
      { id: '3', name: '定制开发标准模板', description: '软件定制开发项目', sections: 15 },
    ],
    '产品类': [
      { id: '4', name: '产品采购标准模板', description: '产品采购类项目', sections: 8 },
    ],
    '两轮车': [
      { id: '5', name: '两轮车标准模板', description: '两轮车相关项目', sections: 10 },
    ],
  };

  const currentTemplates = mockTemplates[selectedType] || [];

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* 头部 */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">模板管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理标书模板</p>
          </div>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium">
            <PlusIcon className="w-5 h-5" />
            新建模板
          </button>
        </div>
      </div>

      {/* Tab页切换 */}
      <div className="bg-white px-6">
        <div className="flex gap-8">
          {projectTypes.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`py-4 border-b-2 transition-colors ${
                selectedType === type
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 模板列表 */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <DocumentDuplicateIcon className="w-10 h-10 text-blue-500" />
                <div className="flex gap-2">
                  <button className="text-gray-400 hover:text-blue-600 transition-colors">
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button className="text-gray-400 hover:text-red-600 transition-colors">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{template.sections} 个章节</span>
                <button className="text-blue-600 hover:text-blue-700 font-medium">
                  使用模板
                </button>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* 空状态 */}
        {!loading && currentTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <DocumentDuplicateIcon className="w-16 h-16 mb-4" />
            <p className="text-lg">暂无{selectedType}模板</p>
            <button className="mt-4 text-blue-600 hover:text-blue-700">
              创建第一个模板
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManagement;
